import api from '@/lib/api';
import type { BulkImportReport } from '@pms/shared';

// ─── Bulk property onboarding (Hotel Groups — Slice 2) ──────────────────────────
//
// Super-admin-only. The operator uploads a flat CSV/XLSX where each row is one
// property, then:
//   1. POST /admin/platform/properties/bulk-import/validate → a per-row preview
//      report ({@link BulkImportReport}) with NO writes.
//   2. POST /admin/platform/properties/bulk-import/commit → creates the valid
//      NEW rows (partial-with-report; idempotent via an `Idempotency-Key` header
//      + the external_ref / name+address natural key). Rows land as DRAFT.
//
// Both are multipart FormData (field name "file", optional "targetTenantId").
// Mirrors `importPropertyPreview` in platform.api.ts: the axios xhr adapter drops
// the default JSON Content-Type for a FormData body and lets the browser set
// multipart/form-data with the correct boundary — so we never set it by hand.

/**
 * Validate an uploaded CSV/XLSX and return the per-row preview report. No writes.
 * `targetTenantId` (optional) attaches every row to one operator; otherwise each
 * row's `operator` column resolves (or would create) its own operator.
 */
export async function validateBulkImport(
  file: File,
  targetTenantId?: string,
): Promise<BulkImportReport> {
  const form = new FormData();
  form.append('file', file);
  if (targetTenantId) form.append('targetTenantId', targetTenantId);
  const { data } = await api.post<BulkImportReport>(
    '/admin/platform/properties/bulk-import/validate',
    form,
  );
  return data;
}

/**
 * Commit the valid NEW rows (partial-with-report). The `idempotencyKey` is minted
 * once per wizard session and sent as the `Idempotency-Key` header so a retried
 * commit replays the first report instead of double-creating. The commit route is
 * throttled server-side (5/min).
 */
export async function commitBulkImport(
  file: File,
  targetTenantId: string | undefined,
  idempotencyKey: string,
): Promise<BulkImportReport> {
  const form = new FormData();
  form.append('file', file);
  if (targetTenantId) form.append('targetTenantId', targetTenantId);
  const { data } = await api.post<BulkImportReport>(
    '/admin/platform/properties/bulk-import/commit',
    form,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  );
  return data;
}
